import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface TireFilters {
  status?: string;
  vehicleId?: string;
  search?: string;
}

export function useTires(companyId: string | undefined, filters?: TireFilters) {
  return useQuery({
    queryKey: ['fleet-tires', companyId, filters],
    queryFn: async () => {
      if (!companyId) return [];
      
      let query = supabase
        .from('fleet_tires')
        .select(`
          *,
          vehicle:fleet_vehicles(id, plate, make, model),
          provider:fleet_providers(id, name)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters?.vehicleId) {
        query = query.eq('current_vehicle_id', filters.vehicleId);
      }

      if (filters?.search) {
        query = query.or(`brand.ilike.%${filters.search}%,model.ilike.%${filters.search}%,serial_number.ilike.%${filters.search}%`);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!companyId
  });
}

export function useCreateTire(companyId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (tire: any) => {
      if (!companyId) throw new Error('Company ID required');
      
      const { data, error } = await supabase
        .from('fleet_tires')
        .insert({
          ...tire,
          company_id: companyId,
          status: 'stock'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-tires', companyId] });
      toast({
        title: "Pneu cadastrado",
        description: "Pneu cadastrado com sucesso.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao cadastrar pneu",
        description: error.message,
        variant: "destructive",
      });
    }
  });
}

export function useUpdateTire(companyId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: any) => {
      const { data, error } = await supabase
        .from('fleet_tires')
        .update(updates)
        .eq('id', id)
        .eq('company_id', companyId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-tires', companyId] });
      toast({
        title: "Pneu atualizado",
        description: "Pneu atualizado com sucesso.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao atualizar pneu",
        description: error.message,
        variant: "destructive",
      });
    }
  });
}

export function useDeleteTire(companyId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('fleet_tires')
        .delete()
        .eq('id', id)
        .eq('company_id', companyId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-tires', companyId] });
      toast({
        title: "Pneu removido",
        description: "Pneu removido com sucesso.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao remover pneu",
        description: error.message,
        variant: "destructive",
      });
    }
  });
}
